import React, { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import * as THREE from 'three';

// Generate particle positions once at module load time (outside render)
const PARTICLE_COUNT = 2000;
const PARTICLE_POSITIONS = (() => {
    const arr = new Float32Array(PARTICLE_COUNT * 3);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
        arr[i * 3] = (Math.random() - 0.5) * 20;
        arr[i * 3 + 1] = (Math.random() - 0.5) * 20;
        arr[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }
    return arr;
})();

// Particle field that drifts slowly
const ParticleField = () => {
    const ref = useRef();

    useFrame((_, delta) => {
        if (ref.current) {
            ref.current.rotation.y += delta * 0.02;
            ref.current.rotation.x += delta * 0.01;
        }
    });

    return (
        <Points ref={ref} positions={PARTICLE_POSITIONS} stride={3} frustumCulled={false}>
            <PointMaterial
                transparent
                color="#4f46e5"
                size={0.02}
                sizeAttenuation
                depthWrite={false}
                opacity={0.35}
            />
        </Points>
    );
};

// Slowly rotating wireframe icosahedron
const FloatingGeometry = () => {
    const meshRef = useRef();

    useFrame((_, delta) => {
        if (meshRef.current) {
            meshRef.current.rotation.x += delta * 0.08;
            meshRef.current.rotation.y += delta * 0.12;
        }
    });

    return (
        // eslint-disable-next-line react/no-unknown-property
        <mesh ref={meshRef} position={[3.5, 0, -3]}>
            {/* eslint-disable-next-line react/no-unknown-property */}
            <icosahedronGeometry args={[1.2, 1]} />
            {/* eslint-disable-next-line react/no-unknown-property */}
            {/* eslint-disable react/no-unknown-property */}
            <meshBasicMaterial
                color="#4f46e5"
                wireframe
                transparent
                opacity={0.12}
            />
            {/* eslint-enable react/no-unknown-property */}
        </mesh>
    );
};

// Slowly rotating torus knot
const FloatingTorusKnot = () => {
    const meshRef = useRef();

    useFrame((_, delta) => {
        if (meshRef.current) {
            meshRef.current.rotation.x += delta * 0.05;
            meshRef.current.rotation.y += delta * 0.09;
        }
    });

    return (
        // eslint-disable-next-line react/no-unknown-property
        <mesh ref={meshRef} position={[-3.5, 0.5, -4]}>
            {/* eslint-disable-next-line react/no-unknown-property */}
            <torusKnotGeometry args={[0.8, 0.2, 80, 16]} />
            {/* eslint-disable react/no-unknown-property */}
            <meshBasicMaterial
                color="#7c3aed"
                wireframe
                transparent
                opacity={0.1}
            />
            {/* eslint-enable react/no-unknown-property */}
        </mesh>
    );
};

const Scene3D = () => {
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 0,
                pointerEvents: 'none',
            }}
        >
            <Canvas
                camera={{ position: [0, 0, 5], fov: 60 }}
                gl={{ antialias: true, alpha: true }}
                style={{ background: 'transparent' }}
            >
                {/* eslint-disable-next-line react/no-unknown-property */}
                <ambientLight intensity={0.1} />
                <ParticleField />
                <FloatingGeometry />
                <FloatingTorusKnot />
            </Canvas>
        </div>
    );
};

export default Scene3D;
